/**
 * Reactive store with event-based reactivity
 * Caches Firestore data locally and emits events on changes
 *
 * Bug fixes vs original:
 * - checked/manual use real booleans (not string 'TRUE'/'FALSE')
 * - formatQuantity no longer truncates whole numbers ending in 0
 * - User settings support (target servings stored in Firestore)
 */

import { db } from './services/db.js';
import { normalizeUnit, parseQuantity, formatQuantity } from './utils/units.js';

class Store {
  constructor() {
    this.recipes = [];
    this.ingredients = [];
    this.inventory = [];
    this.mealPlan = [];
    this.shoppingList = [];
    this.priceHistory = [];
    this.settings = { targetServings: 2.5 };
    this.listeners = {};
  }

  // === Data Loading ===

  async loadAll() {
    await Promise.all([
      this.loadRecipes(),
      this.loadIngredients(),
      this.loadInventory(),
      this.loadMealPlan(),
      this.loadShoppingList(),
      this.loadPriceHistory(),
      this.loadSettings(),
    ]);
  }

  async loadRecipes() {
    this.recipes = await db.read('recipes');
    this.emit('recipes-updated', this.recipes);
    return this.recipes;
  }

  async loadIngredients() {
    this.ingredients = await db.read('ingredients');
    this.emit('ingredients-updated', this.ingredients);
    return this.ingredients;
  }

  async loadInventory() {
    this.inventory = await db.read('inventory');
    this.emit('inventory-updated', this.inventory);
    return this.inventory;
  }

  async loadMealPlan() {
    this.mealPlan = await db.read('meal_plans');
    this.emit('mealplan-updated', this.mealPlan);
    return this.mealPlan;
  }

  async loadShoppingList() {
    this.shoppingList = await db.read('shopping_list');
    this.emit('shoppinglist-updated', this.shoppingList);
    return this.shoppingList;
  }

  async loadPriceHistory() {
    this.priceHistory = await db.read('price_history');
    this.emit('pricehistory-updated', this.priceHistory);
    return this.priceHistory;
  }

  async loadSettings() {
    try {
      const settingsDoc = await db.readOne('settings', 'preferences');
      if (settingsDoc) {
        this.settings = { ...this.settings, ...settingsDoc };
      }
    } catch {
      // Settings collection may not exist yet - use defaults
    }
    this.emit('settings-updated', this.settings);
    return this.settings;
  }

  // === Settings ===

  getSettings() {
    return this.settings;
  }

  async updateSettings(updates) {
    this.settings = { ...this.settings, ...updates };
    await db.create('settings', this.settings, 'preferences');
    this.emit('settings-updated', this.settings);
  }

  // === Recipe Methods ===

  getRecipes() {
    return this.recipes;
  }

  getRecipe(id) {
    return this.recipes.find((r) => r.id === id);
  }

  async addRecipe(recipe) {
    const id = db.generateId('r');
    await db.create('recipes', recipe, id);
    await this.loadRecipes();
    return { id, ...recipe };
  }

  async updateRecipe(id, updates) {
    await db.update('recipes', id, updates);
    await this.loadRecipes();
  }

  async deleteRecipe(id) {
    await db.delete('recipes', id);
    await db.deleteWhere('ingredients', [['recipe_id', '==', id]]);
    await this.loadRecipes();
    await this.loadIngredients();
  }

  // === Ingredient Methods ===

  getRecipeIngredients(recipeId) {
    return this.ingredients.filter((i) => i.recipe_id === recipeId);
  }

  async addIngredient(ingredient) {
    const id = db.generateId('ing');
    await db.create('ingredients', ingredient, id);
    await this.loadIngredients();
  }

  async saveRecipeIngredients(recipeId, newIngredients) {
    await db.deleteWhere('ingredients', [['recipe_id', '==', recipeId]]);

    const operations = newIngredients.map((ingredient) => ({
      type: 'set',
      collection: 'ingredients',
      id: db.generateId('ing'),
      data: ingredient,
    }));

    if (operations.length > 0) {
      await db.batchWrite(operations);
    }
    await this.loadIngredients();
  }

  // === Inventory Methods ===

  getInventory() {
    return this.inventory;
  }

  getInventoryItem(item) {
    return this.inventory.find((i) => i.item.toLowerCase() === item.toLowerCase());
  }

  async addInventoryItem(item) {
    const id = db.generateId('inv');
    await db.create('inventory', item, id);
    await this.loadInventory();
  }

  async updateInventoryItem(itemName, updates) {
    const items = await db.query('inventory', [['item', '==', itemName]]);
    if (items.length > 0) {
      await db.update('inventory', items[0].id, updates);
      await this.loadInventory();
    }
  }

  async deleteInventoryItem(itemName) {
    await db.deleteWhere('inventory', [['item', '==', itemName]]);
    await this.loadInventory();
  }

  // === Meal Plan Methods ===

  getMealPlanForWeek(weekStart) {
    return this.mealPlan.filter((m) => m.week_start === weekStart);
  }

  async addMealToPlan(meal) {
    const id = db.generateId('meal');
    await db.create('meal_plans', meal, id);
    await this.loadMealPlan();
  }

  async updateMealInPlan(weekStart, day, updates) {
    const meals = await db.query('meal_plans', [
      ['week_start', '==', weekStart],
      ['day', '==', day],
    ]);

    if (meals.length === 0) {
      await this.addMealToPlan({ week_start: weekStart, day, ...updates });
    } else if (meals.length === 1) {
      await db.update('meal_plans', meals[0].id, updates);
      await this.loadMealPlan();
    } else {
      // Clean up duplicates
      for (let i = 1; i < meals.length; i++) {
        await db.delete('meal_plans', meals[i].id);
      }
      await db.update('meal_plans', meals[0].id, updates);
      await this.loadMealPlan();
    }
  }

  async clearMealFromPlan(weekStart, day) {
    await db.deleteWhere('meal_plans', [
      ['week_start', '==', weekStart],
      ['day', '==', day],
    ]);
    await this.loadMealPlan();
  }

  async clearWeek(weekStart) {
    await db.deleteWhere('meal_plans', [['week_start', '==', weekStart]]);
    await db.deleteWhere('shopping_list', [['week_start', '==', weekStart], ['manual', '==', false]]);
    await this.loadMealPlan();
    await this.loadShoppingList();
  }

  // === Shopping List Methods ===

  getShoppingListForWeek(weekStart) {
    return this.shoppingList.filter((s) => s.week_start === weekStart);
  }

  async generateShoppingList(weekStart) {
    const weekMeals = this.getMealPlanForWeek(weekStart);
    if (weekMeals.length === 0) {
      throw new Error('No meals planned for this week');
    }

    const aggregated = {};
    const targetServings = this.settings.targetServings || 2.5;

    for (const meal of weekMeals) {
      const recipe = this.getRecipe(meal.recipe_id);
      if (!recipe) continue;

      const ingredients = this.getRecipeIngredients(meal.recipe_id);
      const servings = parseFloat(recipe.servings) || 4;
      const multiplier = targetServings / servings;

      for (const ingredient of ingredients) {
        const itemName = ingredient.item.toLowerCase().trim();
        const normalizedUnit = normalizeUnit(ingredient.unit);
        const key = `${itemName}|${normalizedUnit}`;
        const quantity = parseQuantity(ingredient.quantity);
        const scaledQuantity = quantity * multiplier;

        if (!aggregated[key]) {
          aggregated[key] = {
            item: ingredient.item,
            quantity: 0,
            unit: normalizedUnit,
            category: ingredient.category,
          };
        }
        aggregated[key].quantity += scaledQuantity;
      }
    }

    const shoppingItems = [];

    for (const item of Object.values(aggregated)) {
      const inventoryItem = this.getInventoryItem(item.item);

      if (!inventoryItem || inventoryItem.status === 'low' || inventoryItem.status === 'out') {
        const typical_price = inventoryItem?.typical_price || '';
        const estimated_price = typical_price
          ? (parseFloat(typical_price) * item.quantity).toFixed(2)
          : '';

        shoppingItems.push({
          week_start: weekStart,
          item: item.item,
          quantity: parseFloat(formatQuantity(item.quantity)),
          unit: item.unit,
          category: item.category,
          estimated_price,
          checked: false,
          manual: false,
        });
      }
    }

    await db.deleteWhere('shopping_list', [['week_start', '==', weekStart]]);

    const operations = shoppingItems.map((item) => ({
      type: 'set',
      collection: 'shopping_list',
      id: db.generateId('shop'),
      data: item,
    }));

    if (operations.length > 0) {
      await db.batchWrite(operations);
    }

    await this.loadShoppingList();
    return shoppingItems;
  }

  async updateShoppingItem(weekStart, item, updates) {
    const items = await db.query('shopping_list', [
      ['week_start', '==', weekStart],
      ['item', '==', item],
    ]);

    if (items.length > 0) {
      await db.update('shopping_list', items[0].id, updates);
      await this.loadShoppingList();
    }
  }

  async addManualShoppingItem(item) {
    const id = db.generateId('shop');
    await db.create('shopping_list', { ...item, manual: true }, id);
    await this.loadShoppingList();
  }

  // === Price History Methods ===

  async logPrices(weekStart, prices) {
    const today = new Date().toISOString().split('T')[0];

    const operations = prices.map((p) => ({
      type: 'set',
      collection: 'price_history',
      id: db.generateId('price'),
      data: { date: today, item: p.item, price: p.price, store: p.store || '' },
    }));

    if (operations.length > 0) {
      await db.batchWrite(operations);
    }
    await this.loadPriceHistory();
  }

  // === Event System ===

  on(event, callback) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => callback(data));
    }
  }
}

export const store = new Store();
